import { useMemo } from 'react'
import _ from 'lodash'

// constants
import { FEATURES, SETTINGS_TYPES } from 'constants/settings'

// components
import SecurityAndPolicy from 'components/settings/SecurityAndPolicy'
import UserProfile from 'components/settings/UserProfile'
import Versions from 'components/settings/Versions'
import Plugins from 'components/settings/Plugins'
import { getComponent, getNavLabelAndIcon, NAV_KEYS } from 'constants/nav'
import usePlugins from './usePlugins'

const useSettingsMenu = () => {
  const pluginState = usePlugins()

  const settingsMenu = useMemo(
    () => [
      {
        label: 'MY SETTINGS',
        group: [
          {
            label: 'My profile',
            value: SETTINGS_TYPES.userProfile,
            icon: 'FaRegUserCircle',
            component: UserProfile,
          },
          {
            label: 'Security and Privacy',
            value: SETTINGS_TYPES.securityAndPrivacy,
            icon: 'MdSecurity',
            component: SecurityAndPolicy,
          },
          {
            label: 'Versions',
            value: SETTINGS_TYPES.versions,
            component: Versions,
          },
        ],
      },
      {
        // ! When adding a new app with a list route and an item detail route, make sure the "return to list" link on the item page points to the settings page (e.g., /settings/workflow) rather than the list page (e.g., /workflows).
        label: 'APP CONFIGURATION',
        group: [
          {
            ...getNavLabelAndIcon(NAV_KEYS.USER_MANAGEMENT),
            value: SETTINGS_TYPES.userManagement,
            component: getComponent(NAV_KEYS.USER_MANAGEMENT),
            isExpanded: true,
            featureFlagKey: FEATURES.USER_MANAGEMENT,
            sidebarRouteEnabled: true,
          },
          {
            ...getNavLabelAndIcon(NAV_KEYS.FORM_DESIGNER),
            value: SETTINGS_TYPES.formDesigner,
            component: getComponent(NAV_KEYS.FORM_DESIGNER),
            isExpanded: true,
            featureFlagKey: FEATURES.JSON_FORM,
            sidebarRouteEnabled: true,
          },
          {
            ...getNavLabelAndIcon(NAV_KEYS.WORKFLOW_AUTOMATION),
            value: SETTINGS_TYPES.workflow,
            component: getComponent(NAV_KEYS.WORKFLOW_AUTOMATION),
            isExpanded: true,
            featureFlagKey: FEATURES.WORKFLOW,
            sidebarRouteEnabled: true,
          },
          {
            ...getNavLabelAndIcon(NAV_KEYS.PROFILES),
            value: SETTINGS_TYPES.profiles,
            component: getComponent(NAV_KEYS.PROFILES),
            isExpanded: true,
            featureFlagKey: FEATURES.ASSET,
            sidebarRouteEnabled: true,
          },
          {
            ...getNavLabelAndIcon(NAV_KEYS.SITES),
            value: SETTINGS_TYPES.sites,
            component: getComponent(NAV_KEYS.SITES),
            isExpanded: true,
            featureFlagKey: FEATURES.SITE,
            sidebarRouteEnabled: true,
          },
          {
            ...getNavLabelAndIcon(NAV_KEYS.DYNAMIC_SCHEDULE),
            value: SETTINGS_TYPES.dynamicScheduling,
            component: getComponent(NAV_KEYS.DYNAMIC_SCHEDULE),
            isExpanded: true,
            featureFlagKey: FEATURES.DYNAMIC_SCHEDULING,
            sidebarRouteEnabled: true,
          },
          {
            ...getNavLabelAndIcon(NAV_KEYS.PLUGINS),
            value: SETTINGS_TYPES.plugins,
            component: Plugins,
            isExpanded: true,
            enabled: !_.isEmpty(pluginState),
            sidebarRouteEnabled: true,
          },
        ],
      },
    ],
    [pluginState]
  )

  const settingsMenuWithoutGroup = useMemo(
    () => _.flatMap(settingsMenu, 'group'),
    [settingsMenu]
  )
  const settingsMenuKeyByValue = useMemo(
    () => _.keyBy(settingsMenuWithoutGroup, 'value'),
    [settingsMenuWithoutGroup]
  )

  return {
    settingsMenu,
    settingsMenuWithoutGroup,
    settingsMenuKeyByValue,
  }
}

export default useSettingsMenu
